// =============================================================================
// components/SplashLogoFill.tsx — the CRYB wordmark that FILLS with liquid.
//
// Crisp, native water-fill: the letters are an SVG mask; a purple liquid with a
// gentle WAVY waterline rises from the bottom, clipped to the letter shapes, so
// the fill stays perfectly inside CRYB (no AI wobble). Above the waterline the
// letters read as a faint outline. Drive with `progress` (0→1) for real load, or
// leave it to self-animate. react-native-svg + reanimated (both proven deps).
// =============================================================================
import React, { useEffect } from 'react';
import { View, StyleProp, ViewStyle } from 'react-native';
import Svg, { Defs, LinearGradient, Stop, Mask, Text as SvgText, Path } from 'react-native-svg';
import Animated, { useSharedValue, useAnimatedProps, withTiming, withRepeat, Easing } from 'react-native-reanimated';
import type { SharedValue } from 'react-native-reanimated';

const AnimatedPath = Animated.createAnimatedComponent(Path);

export interface SplashLogoFillProps {
  size?: number;                       // cap height, default 76
  progress?: SharedValue<number>;      // external 0→1 fill; self-animates if omitted
  style?: StyleProp<ViewStyle>;
}

export default function SplashLogoFill({ size = 76, progress, style }: SplashLogoFillProps) {
  const W = size * 4.3;
  const H = size * 1.4;
  const baselineY = H * 0.74;
  const tracking = size * 0.05;

  const internal = useSharedValue(0);
  const phase = useSharedValue(0);
  useEffect(() => {
    if (!progress) internal.value = withTiming(1, { duration: 4000, easing: Easing.inOut(Easing.cubic) });
    phase.value = withRepeat(withTiming(Math.PI * 2, { duration: 1500, easing: Easing.linear }), -1, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const p = progress ?? internal;

  // Rising liquid with a wavy top edge, built inline in the worklet (no external
  // worklet fn). Wave amplitude eases to near-flat as the fill completes.
  const liquidProps = useAnimatedProps(() => {
    const v = Math.max(0, Math.min(1, p.value));
    // Waterline tracks the letters EXACTLY so the fill fraction == v (== the %).
    // Letters span from the baseline (H*0.74) up by one cap height (~0.72*size);
    // at v=0 the surface sits at the baseline (0% filled), at v=1 it reaches the
    // cap top (100% filled).
    const capH = size * 0.72;
    const top = H * 0.74 - v * capH;
    const amp = (1 - v) * (size * 0.045) + 0.8; // wave flattens as it tops out
    const steps = 22;
    let d = `M 0 ${H} L 0 ${top}`;
    for (let i = 0; i <= steps; i++) {
      const x = (W * i) / steps;
      const y = top + Math.sin((i / steps) * 2.4 * Math.PI * 2 + phase.value) * amp;
      d += ` L ${x} ${y}`;
    }
    d += ` L ${W} ${H} Z`;
    return { d };
  });

  return (
    <View style={[{ width: W, height: H }, style]} accessibilityRole="header" accessibilityLabel="CRYB">
      <Svg width={W} height={H} viewBox={`0 0 ${W} ${H}`}>
        <Defs>
          <LinearGradient id="crybLiquid" x1="0" y1="0" x2="0" y2="1">
            <Stop offset="0" stopColor="#FFFFFF" stopOpacity={0.72} />
            <Stop offset="1" stopColor="#FFFFFF" stopOpacity={0.5} />
          </LinearGradient>
          <Mask id="crybLetters">
            <SvgText x={W / 2} y={baselineY} textAnchor="middle" fontSize={size} fontWeight="800" letterSpacing={tracking} fill="#FFFFFF">CRYB</SvgText>
          </Mask>
        </Defs>
        {/* Faint empty letters (what's not filled yet) — light tone-on-tone. */}
        <SvgText x={W / 2} y={baselineY} textAnchor="middle" fontSize={size} fontWeight="800" letterSpacing={tracking} fill="#FFFFFF" fillOpacity={0.26}>CRYB</SvgText>
        {/* Rising liquid, clipped to the letter shapes. */}
        <AnimatedPath animatedProps={liquidProps} fill="url(#crybLiquid)" mask="url(#crybLetters)" />
      </Svg>
    </View>
  );
}
